import { useState } from "react";
import type { CheckClientNameResponse } from "@salesboard/contracts";
import { CLIENT_NAME_MAX } from "@salesboard/contracts";
import { Field, Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FormDialog } from "@/components/ui/FormDialog";
import { useT } from "@/i18n";
import { useCheckClientName, useCreateClient, useRenameClient } from "./hooks";

type Mode = { kind: "create" } | { kind: "rename"; id: string; name: string; version: number };

/**
 * Create or rename a client, with the duplicate warning in front of creation.
 *
 * The warning **lists the matches and offers to create anyway** — it never blocks. Naming
 * them is the point: the user recognises "Acme s.r.o." and picks it rather than creating
 * a second Acme.
 */
export function ClientFormDialog({
  open,
  onOpenChange,
  mode,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
  onCreated?: (client: { id: string; name: string }) => void;
}) {
  const t = useT();
  const [name, setName] = useState(mode.kind === "rename" ? mode.name : "");
  const [error, setError] = useState<string>();
  const [matches, setMatches] = useState<CheckClientNameResponse["matches"]>([]);

  const checkName = useCheckClientName();
  const create = useCreateClient();
  const rename = useRenameClient(mode.kind === "rename" ? mode.id : "");

  const pending = create.isPending || rename.isPending || checkName.isPending;

  const reset = () => {
    setMatches([]);
    setError(undefined);
  };

  const submit = async (force = false) => {
    setError(undefined);

    if (!name.trim()) {
      setError(t("clientForm.nameRequired"));
      return;
    }

    if (mode.kind === "rename") {
      await rename.mutateAsync({ name, version: mode.version });
      onOpenChange(false);
      return;
    }

    // Check on submit rather than while typing: a warning that appears mid-word is noise,
    // and the decision only matters at the moment of creating.
    if (!force) {
      const result = await checkName.mutateAsync(name);
      if (result.matches.length > 0) {
        setMatches(result.matches);
        return;
      }
    }

    const client = await create.mutateAsync({ name });
    onCreated?.(client);
    onOpenChange(false);
    setName("");
    reset();
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title={mode.kind === "create" ? t("clientForm.createTitle") : t("clientForm.renameTitle")}
      description={mode.kind === "create" ? t("clientForm.createDescription") : undefined}
      submitLabel={mode.kind === "create" ? t("clientForm.createSubmit") : t("clientForm.renameSubmit")}
      pending={pending}
      error={error}
      onSubmit={(event) => {
        event.preventDefault();
        void submit(false);
      }}
    >
      <Field label={t("clientForm.name")} htmlFor="client-name">
        <Input
          id="client-name"
          value={name}
          maxLength={CLIENT_NAME_MAX}
          // The rule's concern is autofocus stealing focus on page load. Inside a modal
          // it is the opposite: Radix moves focus into the dialog regardless, and this
          // only chooses *which* element receives it. Landing on the single input is what
          // both a keyboard and a screen-reader user want here.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onChange={(event) => {
            setName(event.target.value);
            if (matches.length) reset();
          }}
        />
      </Field>

      {matches.length > 0 && (
        <div className="border-warning/40 bg-warning/10 space-y-2 rounded-md border p-3">
          <p className="text-sm font-medium">{t("clientForm.duplicateTitle")}</p>
          <ul className="space-y-1 text-sm">
            {matches.map((match) => (
              <li key={match.id} className="flex items-center gap-2">
                <span>{match.name}</span>
                {!match.active && <span className="text-muted-foreground text-xs">{t("clientForm.inactiveTag")}</span>}
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => void submit(true)} disabled={pending}>
              {t("clientForm.createAnyway")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}
    </FormDialog>
  );
}
